import { Component, EventEmitter, OnDestroy, OnInit, Output, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { DstRolloverModalService } from './dst-rollover-modal.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-dst-rollover-modal',
    templateUrl: './dst-rollover-modal.component.html',
    styleUrls: ['./dst-rollover-modal.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class DstRolloverModalComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  filterParams: any = {};
  baseURLs: any = { admin: '', api: '', custom: '' };
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  modalConfig: ConfirmationModalConfig = {
    title: 'Confirm RollOver',
    message: 'Are you sure you want to proceed with RollOver?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast',
    loading: false,
    loadingText: 'Processing...'
  };
  showModal = true;

  private isInitialized = false;

  constructor(
    private gtwSnackbarService: GtwSnackbarService,
    private dstRolloverModalService: DstRolloverModalService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
      })
    );
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.GENERAL_CONFIG = generalConfigFromGlobal(this.gtwGlobal);
    this.baseURLs = this.GENERAL_CONFIG;
    this.globalService = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    this.filterParams = normalizeFilterParams(activeScreen);
    this.tryInit();
  }

  private tryInit(): void {
    if (this.isInitialized) return;
    if (!this.globalService || !this.GENERAL_CONFIG || !this.filterParams || Object.keys(this.filterParams).length === 0) return;
    this.isInitialized = true;
  }

  onConfirm(): void {
    this.modalConfig.loading = true;
    const baseUrl = this.GENERAL_CONFIG?.base_url || '';
    const ssoId = this.gtwApp.USER_SETTINGS?.user?.sso_id || this.globalService?.sso_id || '';
    const taxYear = this.filterParams?.tax_year ?? this.globalService?.tax_year;

    this.dstRolloverModalService.triggerRollover(baseUrl, ssoId, taxYear).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (res: any) => {
        this.modalConfig.loading = false;
        // Legacy keyed off callSuccess alone: a successful rollover carries no
        // errorMessage, so requiring errorMessage === 'null' reported a failure.
        if (res?.callSuccess === '1' || res?.callSuccess === 1) {
          this.gtwSnackbarService.openSnackBar('Data RollOver Successful.', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        } else {
          this.gtwSnackbarService.openSnackBar(res?.errorMessage || 'Rollover failed.', 'gtw-snackbar--red');
        }
      },
      () => {
        this.modalConfig.loading = false;
        this.gtwSnackbarService.openSnackBar('Error during rollover. Please try again.', 'gtw-snackbar--red');
      }
    );
  }

  onCancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
